import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import {
  FinishingLevel,
  FinishingLevelDtoDelekComponent,
  FinishingLevelEngine,
  getEcommFinishingLevels,
} from "api"
import { fetchAccessories } from "components/shopFeatures/accessories/accessoriesSlice"
import { fetchColors } from "components/shopFeatures/color/colorSlice"
import {
  updateLicensingFee,
  updatePriceAndURL,
} from "components/shopFeatures/wizard/wizardSlice"
import { createAsyncAppThunk } from "store"

const name = "detailing"

export type DetailingState = {
  engines: FinishingLevelEngine[]
  detailings: FinishingLevelDtoDelekComponent[]
  selectedDetailingCode: FinishingLevel["code"]
  color: string
  detailing: string
  model: string
  referer: string
}

const initialState: DetailingState = {
  engines: [],
  detailings: [],
  selectedDetailingCode: "",
  color: "",
  detailing: "",
  model: "",
  referer: "",
}

type fetchDetailingArg = { modelId: string; reselectCurrent?: boolean }
export const fetchDetailings = createAsyncAppThunk(
  `${name}/fetchDetailings`,
  async (
    { modelId, reselectCurrent }: fetchDetailingArg,
    { dispatch, getState }
  ) => {
    const {
      data: { body: engines },
    } = await getEcommFinishingLevels(parseInt(modelId))

    if (reselectCurrent) {
      // Reselect the currently selected detailing to fetch all the required data
      const detailingToSelect =
        getState().detailing.selectedDetailingCode ||
        engines[0].finishingLevels[0].details.code
      await dispatch(setSelectedDetailing(detailingToSelect))
    }
    //get all finsihingLevels for all engines in one array
    const detailings = engines.reduce(
      (acc, engine) => [...acc, ...engine.finishingLevels],
      []
    )
    return { engines, detailings }
  }
)

export const setSelectedDetailing = createAsyncAppThunk(
  `${name}/setSelectedDetailing`,
  async (detailingCode: string, { getState, dispatch }) => {
    const { detailings } = getState().detailing

    // If provided detailing doesn't exist, then we select the first one
    if (
      detailings.length > 0 &&
      !detailings.some((d) => d.details.code === detailingCode)
    ) {
      await dispatch(setSelectedDetailing(detailings[0].details.code))
      return
    }
    await Promise.all([
      dispatch(updatePriceAndURL()),
      dispatch(fetchColors(detailingCode)),
      dispatch(fetchAccessories(detailingCode)),
      dispatch(updateLicensingFee(detailingCode)),
    ])
  }
)

const detailing = createSlice({
  name,
  initialState,
  reducers: {
    setColor(state, { payload }: PayloadAction<DetailingState["color"]>) {
      state.color = payload
    },
    setModel(state, { payload }: PayloadAction<DetailingState["model"]>) {
      state.model = payload
    },
    setDetail(state, { payload }: PayloadAction<DetailingState["detailing"]>) {
      state.detailing = payload
    },
    setReferer(state, { payload }: PayloadAction<DetailingState["referer"]>) {
      state.referer = payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDetailings.fulfilled, (state, { payload }) => {
        state.engines = payload.engines
        state.detailings = payload.detailings
      })
      .addCase(setSelectedDetailing.pending, (state, { meta }) => {
        if (!meta.arg) throw new Error("No detailing code provided")
        state.selectedDetailingCode = meta.arg
      })
  },
})

export const { setModel, setColor, setDetail, setReferer } = detailing.actions

export default detailing.reducer
